/**
 * Iter134 · Panel de diagnóstico + control de Twilio WhatsApp.
 *
 * Muestra:
 *  1. Estado de credenciales (sandbox vs business, webhook, from)
 *  2. Últimos mensajes REALES devueltos por la API Twilio (con
 *     error_code + error_message).
 *  3. Widget de opt-in: guarda el keyword del sandbox + genera link
 *     wa.me pre-cargado ("join <keyword>") para compartir con operadores.
 *  4. Test-send: envía un mensaje real a un número específico y muestra
 *     el resultado exacto.
 *  5. Matriz de opt-in por empleado (basado en inbound en 3 días).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  WhatsappLogo, ArrowClockwise, PaperPlaneTilt, ShieldCheck, ShieldWarning,
  CheckCircle, WarningCircle, Copy, ArrowSquareOut, FloppyDisk, KeyReturn,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const TWILIO_ERROR_HINTS = {
  63038: "Sandbox alcanzó el tope diario (5 msg/día en trial). Este operador debe hacer opt-in enviando 'join <keyword>' o hay que subir a Business Sender.",
  63007: "El destinatario no ha hecho opt-in al sandbox. Pídele que envíe 'join <keyword>' al +1 (415) 523-8886.",
  63016: "Fuera de la ventana de 24h. El destinatario debe responder cualquier mensaje al sandbox o aprobar una plantilla.",
  63018: "Número no válido en WhatsApp. Verifica que sea E.164 (+52...) y que tenga cuenta WhatsApp.",
  21211: "Formato de número inválido — usa E.164 (+52...).",
  63003: "Cuenta suspendida o bloqueada por Twilio.",
};

export default function TwilioDiagnosticsPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [savingKw, setSavingKw] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBody, setTestBody] = useState("Prueba UG Management ✅ Este es un mensaje de test.");
  const [testResult, setTestResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [matrix, setMatrix] = useState(null);
  const [loadingMatrix, setLoadingMatrix] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/twilio/whatsapp/status");
      setStatus(data);
      setKeyword(data.sandbox_keyword || "");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  const loadMatrix = useCallback(async () => {
    setLoadingMatrix(true);
    try {
      const { data } = await api.get("/twilio/whatsapp/opt-in-matrix");
      setMatrix(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoadingMatrix(false); }
  }, []);

  useEffect(() => { load(); loadMatrix(); }, [load, loadMatrix]);

  const saveKeyword = async () => {
    setSavingKw(true);
    try {
      await api.put("/twilio/whatsapp/sandbox-config", { keyword });
      toast.success("Keyword guardado");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingKw(false); }
  };

  const runTestSend = async () => {
    if (!testTo.startsWith("+")) {
      toast.error("Usa formato internacional (+52...)");
      return;
    }
    setSending(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/twilio/whatsapp/test-send", {
        to: testTo, body: testBody,
      });
      setTestResult(data);
      if (data.ok) toast.success(`Enviado — Twilio status: ${data.status}`);
      else toast.warning(`Twilio rechazó: ${data.reason || "error"}`);
      // refresh
      setTimeout(load, 1500);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const copyOptInMessage = async () => {
    const kw = status?.sandbox_keyword || keyword;
    if (!kw) { toast.error("Guarda primero el keyword"); return; }
    try {
      await navigator.clipboard.writeText(`join ${kw}`);
      toast.success("Copiado: join " + kw);
    } catch {
      toast.info(`Copia manual: join ${kw}`);
    }
  };

  const optedInPct = useMemo(() => {
    if (!matrix || !matrix.total) return 0;
    return Math.round((matrix.opted_in / matrix.total) * 100);
  }, [matrix]);

  if (loading) {
    return <div className="p-4 text-sm text-slate-400 italic">Cargando diagnóstico Twilio…</div>;
  }

  return (
    <div className="space-y-4" data-testid="twilio-diagnostics-panel">
      {/* Header + config summary */}
      <div className={`rounded-lg p-4 text-white ${status?.sandbox
        ? "bg-gradient-to-br from-amber-800 via-amber-900 to-slate-900 border border-amber-600"
        : "bg-gradient-to-br from-emerald-800 via-emerald-900 to-slate-900 border border-emerald-600"}`}>
        <div className="flex items-center gap-2 mb-2">
          <WhatsappLogo size={22} weight="fill" />
          <h3 className="font-heading font-black text-lg">
            Twilio WhatsApp · Diagnóstico
          </h3>
          <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
            status?.sandbox ? "bg-amber-400/30 text-amber-100"
                             : "bg-emerald-400/30 text-emerald-100"
          }`}>
            {status?.sandbox ? "Modo Sandbox (trial)" : "Business Sender"}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-[11px]">
          <div className="bg-white/10 rounded px-2 py-1.5">
            <div className="opacity-70 uppercase text-[9px] font-bold">Configurado</div>
            <div className="font-bold flex items-center gap-1">
              {status?.configured
                ? <><CheckCircle size={13} weight="fill" className="text-emerald-300" /> Sí</>
                : <><WarningCircle size={13} weight="fill" className="text-red-300" /> No</>}
            </div>
          </div>
          <div className="bg-white/10 rounded px-2 py-1.5">
            <div className="opacity-70 uppercase text-[9px] font-bold">FROM</div>
            <div className="font-mono">{status?.from_number || "—"}</div>
          </div>
          <div className="bg-white/10 rounded px-2 py-1.5">
            <div className="opacity-70 uppercase text-[9px] font-bold">Account SID</div>
            <div className="font-mono">{status?.account_sid_prefix ? `${status.account_sid_prefix}…` : "—"}</div>
          </div>
          <div className="bg-white/10 rounded px-2 py-1.5">
            <div className="opacity-70 uppercase text-[9px] font-bold">Webhook</div>
            <div className="font-mono text-[10px] truncate">{status?.webhook_url || "—"}</div>
          </div>
        </div>
      </div>

      {/* Sandbox opt-in config */}
      {status?.sandbox && (
        <div className="bg-white border-2 border-amber-300 rounded-lg p-4" data-testid="twilio-sandbox-opt-in">
          <div className="flex items-center gap-2 mb-2">
            <ShieldWarning size={18} weight="fill" className="text-amber-600" />
            <h4 className="font-heading font-black text-slate-900">Opt-in del Sandbox</h4>
          </div>
          <p className="text-xs text-slate-600 mb-3">
            En sandbox cada operador debe autorizar UNA vez enviando <code className="bg-slate-100 px-1 rounded">join &lt;keyword&gt;</code> al
            <b> +1 (415) 523-8886</b>. Consigue tu keyword en <a href="https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn"
              target="_blank" rel="noreferrer" className="text-blue-800 underline inline-flex items-center gap-0.5">
              Twilio Console → Try it Out → WhatsApp <ArrowSquareOut size={11} weight="bold" />
            </a>. Pégalo aquí y comparte el link con tus operadores.
          </p>
          <div className="flex gap-2 mb-3">
            <div className="flex-1">
              <Label className="text-[10px] uppercase font-bold text-slate-500">Sandbox keyword</Label>
              <Input value={keyword}
                      onChange={(e) => setKeyword(e.target.value.toLowerCase())}
                      placeholder="ej. elephant-flower"
                      className="h-9 font-mono"
                      data-testid="twilio-keyword-input" />
            </div>
            <div className="flex items-end">
              <Button onClick={saveKeyword} disabled={savingKw}
                       className="bg-amber-700 hover:bg-amber-800 gap-1.5"
                       data-testid="twilio-keyword-save">
                <FloppyDisk size={14} weight="bold" />
                {savingKw ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </div>

          {status?.wa_me_optin_url && (
            <div className="bg-emerald-50 border border-emerald-300 rounded p-3">
              <p className="text-xs font-bold text-emerald-900 mb-2 flex items-center gap-1">
                <CheckCircle size={13} weight="fill" /> Link de opt-in listo para compartir
              </p>
              <div className="flex flex-wrap gap-2 items-center">
                <a href={status.wa_me_optin_url} target="_blank" rel="noreferrer"
                    className="text-xs font-mono bg-white border border-slate-300 rounded px-2 py-1 hover:bg-slate-50 inline-flex items-center gap-1 flex-1 min-w-0 truncate"
                    data-testid="twilio-wa-me-link">
                  <ArrowSquareOut size={12} weight="bold" className="shrink-0" />
                  <span className="truncate">{status.wa_me_optin_url}</span>
                </a>
                <Button size="sm" variant="outline" onClick={() => {
                  navigator.clipboard.writeText(status.wa_me_optin_url).then(() => toast.success("Link copiado"));
                }} className="gap-1 h-8">
                  <Copy size={13} weight="bold" /> Copiar
                </Button>
                <Button size="sm" variant="outline" onClick={copyOptInMessage}
                         className="gap-1 h-8" data-testid="twilio-copy-optin-msg">
                  <KeyReturn size={13} weight="bold" /> Copiar "join {status.sandbox_keyword}"
                </Button>
              </div>
              <p className="text-[10px] text-emerald-800/80 italic mt-2">
                Comparte este link por WhatsApp/SMS con cada operador. Al tocarlo se abre WhatsApp con el mensaje pre-cargado — solo aprieta enviar.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Test send */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <PaperPlaneTilt size={18} weight="fill" className="text-blue-800" />
          <h4 className="font-heading font-black text-slate-900">Envío de prueba (test-send)</h4>
        </div>
        <p className="text-xs text-slate-600 mb-3">
          Envía un mensaje real a un número y ve el resultado EXACTO devuelto por Twilio (incluye error_code para diagnosticar).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <div className="md:col-span-1">
            <Label className="text-[10px] uppercase font-bold text-slate-500">A (E.164)</Label>
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)}
                    placeholder="+525551234567" className="h-9 font-mono"
                    data-testid="twilio-test-to" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase font-bold text-slate-500">Mensaje</Label>
            <Textarea value={testBody} onChange={(e) => setTestBody(e.target.value)}
                       rows={2} className="text-xs" data-testid="twilio-test-body" />
          </div>
        </div>
        <Button onClick={runTestSend} disabled={sending || !testTo}
                 className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                 data-testid="twilio-test-send-btn">
          <PaperPlaneTilt size={14} weight="bold" /> {sending ? "Enviando…" : "Enviar prueba"}
        </Button>
        {testResult && (
          <div className={`mt-3 rounded p-3 text-xs border ${
            testResult.ok && !testResult.error_code
              ? "bg-emerald-50 border-emerald-300"
              : "bg-red-50 border-red-300"
          }`} data-testid="twilio-test-result">
            <div className="font-bold mb-1 flex items-center gap-1">
              {testResult.ok && !testResult.error_code
                ? <><CheckCircle size={13} weight="fill" className="text-emerald-700" /> Entregado a Twilio · status={testResult.status}</>
                : <><WarningCircle size={13} weight="fill" className="text-red-700" /> Fallo · {testResult.reason || `error_code=${testResult.error_code}`}</>}
            </div>
            {testResult.sid && <div className="font-mono text-[10px]">SID: {testResult.sid}</div>}
            {testResult.error_code && (
              <>
                <div className="font-mono text-[10px]">error_code: {testResult.error_code}</div>
                {TWILIO_ERROR_HINTS[testResult.error_code] && (
                  <div className="mt-1 text-[11px] italic text-slate-700">
                    💡 {TWILIO_ERROR_HINTS[testResult.error_code]}
                  </div>
                )}
              </>
            )}
            {testResult.error_message && (
              <div className="text-[10px] mt-1">msg: {testResult.error_message}</div>
            )}
            {testResult.detail && (
              <pre className="text-[10px] mt-2 whitespace-pre-wrap bg-white/50 p-2 rounded">{testResult.detail}</pre>
            )}
          </div>
        )}
      </div>

      {/* Live Twilio API messages */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <ArrowClockwise size={16} weight="fill" className="text-slate-600" />
          <h4 className="font-heading font-black text-slate-900">Últimos mensajes (Twilio API)</h4>
          <Button size="sm" variant="outline" onClick={load} className="ml-auto h-7 gap-1">
            <ArrowClockwise size={12} weight="bold" /> Recargar
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="twilio-live-table">
            <thead className="bg-slate-100 text-slate-700 uppercase text-[9px]">
              <tr>
                <th className="px-2 py-1.5 text-left">Fecha</th>
                <th className="px-2 py-1.5 text-left">Para</th>
                <th className="px-2 py-1.5 text-left">Body</th>
                <th className="px-2 py-1.5 text-center">Status</th>
                <th className="px-2 py-1.5 text-center">Error</th>
              </tr>
            </thead>
            <tbody>
              {!status?.twilio_live_messages?.length && (
                <tr><td colSpan={5} className="text-center py-3 text-slate-400 italic">Sin mensajes registrados en Twilio.</td></tr>
              )}
              {(status?.twilio_live_messages || []).map((m) => (
                <tr key={m.sid} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-2 py-1 font-mono text-[10px] whitespace-nowrap">{(m.date_sent || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-2 py-1 font-mono">{m.to || "—"}</td>
                  <td className="px-2 py-1 max-w-[280px] truncate" title={m.body_preview}>{m.body_preview}</td>
                  <td className="px-2 py-1 text-center">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                      m.status === "delivered" || m.status === "read" ? "bg-emerald-100 text-emerald-900"
                        : m.status === "failed" || m.status === "undelivered" ? "bg-red-100 text-red-900"
                        : "bg-slate-100 text-slate-700"
                    }`}>{m.status}</span>
                  </td>
                  <td className="px-2 py-1 text-center font-mono text-[10px]" title={m.error_message || ""}>
                    {m.error_code ? <span className="text-red-700 font-bold">{m.error_code}</span> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {status?.live_error && (
          <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 p-2 rounded">
            No pudimos consultar Twilio API en vivo: {status.live_error}
          </div>
        )}
      </div>

      {/* Opt-in matrix */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={16} weight="fill" className="text-emerald-700" />
          <h4 className="font-heading font-black text-slate-900">Matriz de opt-in (últimos 3 días)</h4>
          <span className="ml-auto text-[11px] text-slate-500">
            {matrix ? `${matrix.opted_in} / ${matrix.total} operadores (${optedInPct}%)` : "…"}
          </span>
          <Button size="sm" variant="outline" onClick={loadMatrix} className="h-7 gap-1">
            <ArrowClockwise size={12} weight="bold" /> Recargar
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 italic mb-2">
          Un operador está "opted-in" si envió al menos un mensaje al sandbox en los últimos 3 días.
          Si el % es bajo → repite el broadcast del link de opt-in. En Business Sender esto no importa (opt-in permanente).
        </p>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-[11px]" data-testid="twilio-optin-matrix">
            <thead className="bg-slate-100 text-slate-700 uppercase text-[9px] sticky top-0">
              <tr>
                <th className="px-2 py-1.5 text-left">Colaborador</th>
                <th className="px-2 py-1.5 text-left">Puesto</th>
                <th className="px-2 py-1.5 text-left">WhatsApp</th>
                <th className="px-2 py-1.5 text-center">Opt-in</th>
                <th className="px-2 py-1.5 text-left">Último inbound</th>
              </tr>
            </thead>
            <tbody>
              {loadingMatrix && (
                <tr><td colSpan={5} className="text-center py-3 text-slate-400 italic">Cargando…</td></tr>
              )}
              {!loadingMatrix && matrix?.rows?.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-2 py-1 font-semibold text-slate-900">{r.name}</td>
                  <td className="px-2 py-1 text-slate-600">{r.position} · {r.crew}</td>
                  <td className="px-2 py-1 font-mono text-[10px]">{r.whatsapp || r.phone || "—"}</td>
                  <td className="px-2 py-1 text-center">
                    {r.opted_in
                      ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-900">✓ SÍ</span>
                      : <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">PENDIENTE</span>}
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-slate-500">
                    {r.last_inbound ? r.last_inbound.slice(0, 19).replace("T", " ") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
